/**
 * Mouseflow tracking helpers.
 *
 * Tags and pageview paths are emitted in Hebrew so the Mouseflow dashboard
 * shows native customer-facing labels. Callers either pass the resolved
 * translated label of a section, or use one of the MF_LABELS Hebrew strings
 * below for static events that have no UI label.
 *
 * The Analyze-Sessions.ps1 script picks up two signals:
 *   - pageView.websitePage matching `/dsr/<name>`  → per-section engagement time
 *   - session.tags containing `נצפה:<name>`         → funnel/section detection
 *
 * Helpers in this module funnel everything through window._mfq, which is safe
 * to call before the Mouseflow bundle loads — calls are queued and replayed
 * once the SDK initializes.
 */

/**
 * Centralized Hebrew labels for static / non-UI-driven events. Use these
 * constants at call sites instead of inline strings so all tags stay consistent.
 */
export const MF_LABELS = {
  // section lifecycle
  sectionViewedPrefix: 'נצפה', // viewed:<section>
  sectionClosedPrefix: 'נסגר', // closed:<section>

  // chat
  chatOpened: 'צ׳אט נפתח',
  chatClosed: 'צ׳אט נסגר',
  chatButtonClick: 'צ׳אט לחיצה',
  chatSectionLabel: 'צ׳אט',

  // share link
  shareLinkCopied: 'שיתוף קישור הועתק',
  shareLinkFailed: 'שיתוף קישור נכשל',

  // modals
  uploadModalOpened: 'מודאל העלאה נפתח',
  uploadSuccess: 'קובץ הועלה בהצלחה',
  menuModalOpened: 'מודאל תפריט נפתח',
  reviewModalOpened: 'מודאל הערות נפתח',
  reviewLinkClick: 'לחיצה על הערות לקוח',

  // navigation
  navBarClick: 'לחיצה על ניווט',

  // purchase / apply
  applyFormClick: 'לחיצה על מעבר לטופס',

  // flow lifecycle
  landingLoaded: 'דף נחיתה נטען',
  landingSectionLabel: 'דף נחיתה',

  // accordion content
  sectionClickPrefix: 'לחיצה בתוך סקציה',
} as const;

declare global {
  interface Window {
    _mfq: unknown[][];
  }
}

const TAG_MAX_LEN = 200;

const queue = (): unknown[][] | null => {
  if (typeof window === 'undefined') return null;
  window._mfq = window._mfq || [];
  return window._mfq;
};

/**
 * Slug-style normalizer that preserves Unicode letters (Hebrew, Latin, etc.)
 * and digits. Whitespace becomes `-`; punctuation/symbols are stripped.
 * Mouseflow tags and pageView paths accept Unicode, and Analyze-Sessions.ps1
 * reads/writes UTF-8, so Hebrew button text survives end-to-end.
 */
const sanitize = (raw: string): string =>
  String(raw)
    .normalize('NFC')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\-_:/ ]/gu, '')
    .slice(0, TAG_MAX_LEN);

export const mfTag = (tag: string): void => {
  const q = queue();
  if (!q || !tag) return;
  q.push(['tag', tag]);
};

export const mfPageView = (path: string): void => {
  const q = queue();
  if (!q || !path) return;
  q.push(['newPageView', path]);
};

export const mfSetVariable = (key: string, value: string | number | null | undefined): void => {
  const q = queue();
  if (!q || !key) return;
  q.push(['setVariable', key, value == null ? '' : String(value)]);
};

/**
 * Record a section view. Pass the **translated Hebrew label** that the customer
 * actually sees on screen (e.g. 'פתיחה', 'מסמכים', 'קבצים מצורפים'). Emits both
 * a virtual pageview at `/dsr/<label>` (Mouseflow tracks per-section engagement
 * time from this) plus a session tag `נצפה:<label>` (Analyze-Sessions.ps1
 * detects sections from this — its regex must accept Unicode letters).
 */
export const mfTrackSection = (sectionDisplayLabel: string): void => {
  const key = sanitize(sectionDisplayLabel);
  if (!key) return;
  mfPageView(`/dsr/${key}`);
  mfTag(`${MF_LABELS.sectionViewedPrefix}:${key}`);
};

/** Tag the close event for a section by its Hebrew display label. */
export const mfTrackSectionClosed = (sectionDisplayLabel: string): void => {
  const key = sanitize(sectionDisplayLabel);
  if (!key) return;
  mfTag(`${MF_LABELS.sectionClosedPrefix}:${key}`);
};

/**
 * Tag a discrete user action. Tag format: `<category>:<action>[:<label>]`.
 *   mfTrackAction('chat', 'opened')             → 'chat:opened'
 *   mfTrackAction('section-opening', 'click',
 *                 'download-attachment')        → 'section-opening:click:download-attachment'
 */
export const mfTrackAction = (category: string, action: string, label?: string): void => {
  const c = sanitize(category);
  const a = sanitize(action);
  if (!c || !a) return;
  const l = label ? sanitize(label) : '';
  mfTag(l ? `${c}:${a}:${l}` : `${c}:${a}`);
};

/** Derive a short, human-readable label from a DOM element for tagging. */
export const mfElementLabel = (el: HTMLElement): string => {
  const direct =
    el.getAttribute('data-mf-label') ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('alt') ||
    (el as HTMLAnchorElement).getAttribute?.('href') ||
    el.textContent?.trim() ||
    el.tagName.toLowerCase();
  return direct.replace(/\s+/g, ' ').trim().slice(0, 100);
};
